import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { FileText, Bus, CreditCard, Package, Check } from 'lucide-react';
import { EditableText } from './EditableComponents';
import { useEditMode } from '../contexts/EditModeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  FileText,
  Bus,
  CreditCard,
  Package
};

export const Services = () => {
  const { isEditMode } = useEditMode();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.services && response.data.services.length > 0) {
        setServices(response.data.services);
      } else {
        // Fallback ai dati di default
        setServices([
          {
            id: 1,
            category: "Valori Bollati",
            icon: "FileText",
            items: ["Marche da Bollo", "Francobolli e Valori Postali"],
            color: "cyan"
          },
          {
            id: 2,
            category: "Trasporti",
            icon: "Bus",
            items: ["Biglietti Autobus Urbani", "Biglietti Autobus Extraurbani"],
            color: "orange"
          },
          {
            id: 3,
            category: "Servizi al Cittadino",
            icon: "CreditCard",
            items: ["Pagamenti Bollettini", "PagoPA", "Pagamento Multe", "Bollo Auto"],
            color: "teal"
          },
          {
            id: 4,
            category: "Punto Posta",
            icon: "Package",
            items: ["Ritiro Pacchi", "Spedizione Pacchi"],
            color: "amber"
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const saveServiceCategory = async (serviceId, newCategory) => {
    try {
      const updatedServices = services.map(s => 
        s.id === serviceId ? { ...s, category: newCategory } : s
      );
      await axios.put(`${API}/settings`, { services: updatedServices });
      setServices(updatedServices);
    } catch (error) {
      console.error('Error saving service:', error);
    }
  };

  const saveServiceItems = async (serviceId, newItems) => {
    try {
      const itemsArray = newItems.split('\n').filter(item => item.trim() !== '');
      const updatedServices = services.map(s => 
        s.id === serviceId ? { ...s, items: itemsArray } : s
      );
      await axios.put(`${API}/settings`, { services: updatedServices });
      setServices(updatedServices);
    } catch (error) {
      console.error('Error saving service items:', error);
    }
  };

  if (loading) {
    return (
      <section id="services" className="section-padding bg-services">
        <div className="container">
          <p className="text-center">Caricamento servizi...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="section-padding bg-services">
      <div className="container">
        <div className="section-header">
          <Badge className="section-badge">I Nostri Servizi</Badge>
          <h2 className="section-title">Punto Servizi Completo</h2>
          <p className="section-description">
            Oltre al tabacco e svapo, offriamo una vasta gamma di servizi per semplificarti la vita
          </p>
        </div>

        <div className="services-grid">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card key={service.id} className="service-card">
                <div className={`service-icon-wrapper service-icon-${service.color}`}>
                  <IconComponent className="w-6 h-6" />
                </div>
                
                {isEditMode ? (
                  <EditableText
                    value={service.category}
                    onSave={(val) => saveServiceCategory(service.id, val)}
                    label="Categoria Servizio"
                  />
                ) : (
                  <h3 className="service-title">{service.category}</h3>
                )}

                {isEditMode ? (
                  <div className="mt-4">
                    <EditableText
                      value={service.items.join('\n')}
                      onSave={(val) => saveServiceItems(service.id, val)}
                      label="Voci Servizio (una per riga)"
                      multiline
                    />
                  </div>
                ) : (
                  <ul className="service-list">
                    {service.items.map((item) => (
                      <li key={`${service.id}-${item}`} className="service-item">
                        <Check className="w-4 h-4 service-check" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            );
          })}
        </div>

        <div className="services-accordion-section">
          <h3 className="accordion-section-title">Domande Frequenti sui Servizi</h3>
          <Accordion type="single" collapsible className="services-accordion">
            <AccordionItem value="item-1">
              <AccordionTrigger>Come posso pagare le multe?</AccordionTrigger>
              <AccordionContent>
                Puoi pagare le multe direttamente nel nostro punto servizi. Porta con te il bollettino e provvederemo al pagamento immediato attraverso il sistema PagoPA.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>Quali biglietti autobus vendete?</AccordionTrigger>
              <AccordionContent>
                Vendiamo biglietti per autobus urbani di Reggio Emilia e biglietti extraurbani per le principali destinazioni della provincia e regione.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>Posso ritirare e spedire pacchi?</AccordionTrigger>
              <AccordionContent>
                Sì! Siamo punto Posta autorizzato. Puoi ritirare pacchi portando documento e codice di ritiro, oppure spedire pacchi tramite il nostro servizio postale.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger>Offrite il servizio bollo auto?</AccordionTrigger>
              <AccordionContent>
                Certamente! Puoi pagare il bollo auto direttamente da noi. Serve la targa del veicolo e provvederemo a tutto noi in pochi minuti.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </section>
  );
};
